import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import { throwIfFailed } from "shared/dialogSubmit";
import MeetingDetailStore from "./MeetingDetailStore";
import type MeetingReviewStore from "./MeetingReviewStore";
import type MeetingRecapStore from "./MeetingRecapStore";
import type MeetingRecordingStore from "./MeetingRecordingStore";
import type MeetingInquiryStore from "./MeetingInquiryStore";

/**
 * The review tab's store (meetingIntelligenceStoreList.ts). Loaded with the
 * meeting: the tab reads it. It follows the review live (#3820) until onExit
 * disposes it.
 */
const loadReview = (rootStore: RootStore, id: string) => {
  const review = (rootStore as unknown as { meetingReviewStore?: MeetingReviewStore }).meetingReviewStore;
  if (!review) return;
  review.setMeetingId(id);
  review.subscribe();
  void review.load();
};

/**
 * The recap tab's store: told which meeting is open, and nothing is read until
 * the person chooses who the recap is for (an act, so not a load here).
 */
const recapStoreOf = (rootStore: RootStore) =>
  (rootStore as unknown as { meetingRecapStore?: MeetingRecapStore }).meetingRecapStore;

/** The Recording card's store: told which meeting is open; a link is minted only when asked for. */
const recordingStoreOf = (rootStore: RootStore) =>
  (rootStore as unknown as { meetingRecordingStore?: MeetingRecordingStore }).meetingRecordingStore;

/** The Client questions tab's store (#4211): loaded with the meeting, as the review is — the tab reads it. */
const loadClientQuestions = (rootStore: RootStore, id: string) => {
  const store = (rootStore as unknown as { meetingInquiryStore?: MeetingInquiryStore }).meetingInquiryStore;
  if (!store) return;
  store.setMeetingId(id);
  void store.load();
};

/**
 * Meeting Detail route — /meetings/:id/:tab?.
 *
 * Hidden, parameterized route (the Meetings list owns the nav entry). The route
 * lifecycle drives the store's load + realtime subscribe/teardown:
 *   - onEnter / onParamsChange → set the id + load the record (embeds
 *     participants[] + actionItems[]) and subscribe to live updates.
 *   - onExit → dispose() tears down the realtime subscription.
 */
const meetingDetailRoute: RouteType[] = [
  {
    name: "MeetingDetail",
    path: "/meetings/:id/:tab?",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Meeting Detail",
        enableRefreshButton: true,
        component: () => import("./MeetingDetail.Header"),
      },
      body: {
        component: () => import("./MeetingDetail.Body"),
        grayBackground: true,
      },
    },
    group: "Meeting Intelligence",
    hidden: true,
    requiresAuth: true,
    requiresPermission: false,
    onEnter: ({ rootStore, params }) => {
      const store = rootStore.readAiMeetingDetailStore as MeetingDetailStore;
      const id = params.id as string;
      if (id) {
        store.setMeetingId(id);
        store.loadMeeting();
        loadReview(rootStore as RootStore, id);
        recapStoreOf(rootStore as RootStore)?.setMeetingId(id);
        recordingStoreOf(rootStore as RootStore)?.setMeetingId(id);
        loadClientQuestions(rootStore as RootStore, id);
      }
    },
    onParamsChange: ({ rootStore, params }) => {
      const store = rootStore.readAiMeetingDetailStore as MeetingDetailStore;
      const id = params.id as string;
      if (id && String(store.meetingId) !== id) {
        store.setMeetingId(id);
        store.loadMeeting();
        loadReview(rootStore as RootStore, id);
        recapStoreOf(rootStore as RootStore)?.setMeetingId(id);
        recordingStoreOf(rootStore as RootStore)?.setMeetingId(id);
        loadClientQuestions(rootStore as RootStore, id);
      }
    },
    onExit: ({ rootStore }) => {
      const store = rootStore.readAiMeetingDetailStore as MeetingDetailStore;
      store?.dispose();
      (rootStore as unknown as { meetingReviewStore?: MeetingReviewStore }).meetingReviewStore?.dispose();
      recapStoreOf(rootStore as RootStore)?.dispose();
      recordingStoreOf(rootStore as RootStore)?.dispose();
      (rootStore as unknown as { meetingInquiryStore?: MeetingInquiryStore }).meetingInquiryStore?.dispose();
    },
    dialogs: [
      {
        // Close one action item, or every ticked one, with a reason. The ONLY
        // write dialog on this page. Operator+ — `onSubmitValidation` re-reads
        // the tier so the submit button cannot arm for a viewer, and
        // `submitClose` re-checks it again before writing.
        name: "CloseActionItemsDialog",
        title: "Close action item",
        component: () => import("./dialogs/CloseActionItems.Body"),
        cancelButton: true,
        submitButtonText: "Close",
        height: 480,
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          // A refused close throws, so the dialog stays open with the reason in
          // its footer (shared/dialogSubmit).
          const store = rootStore.readAiMeetingDetailStore as MeetingDetailStore;
          throwIfFailed(
            await store.submitClose(),
            store.closeError,
            "The action item could not be closed."
          );
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.readAiMeetingDetailStore as MeetingDetailStore;
          return store.isCloseFormValid && store.canWrite;
        },
      },
    ],
  },
];

export default meetingDetailRoute;
